export const ARTICLES = [
  {
    slug: "anxiety-first-whispers",
    title: "When your chest feels too small: naming anxiety's first whispers",
    category: "Anxiety & Overwhelm",
    excerpt: "Anxiety rarely arrives shouting. It hums — a tighter jaw, a shorter breath, a list that never ends. Learning its first language is half the quieting.",
    read_time: "6 min",
    author: "The Back of the Mind team",
    published_at: "2025-11-04",
    content: [
      "Anxiety almost never begins with panic. It begins with whispers: you reread an email three times before sending, your shoulders climb toward your ears in meetings, sleep takes longer than it used to.",
      "The first tool is naming. A feeling named is a feeling that begins to move. Try saying it plainly: 'This is anxiety. It is here because something matters to me.' That sentence alone lowers the volume.",
      "Second, follow the breath instead of the thought. Anxiety is a body event wearing a costume of thoughts. Four seconds in, six seconds out, for ten rounds — the costume usually slips.",
      "Finally, shrink the frame. Overwhelm is a form of time-travel into every deadline at once. Ask: what is the very next small thing? Do that. The chest, slowly, gets its room back."
    ]
  },
  {
    slug: "sunday-scaries-protocol",
    title: "The Sunday scaries: a gentle protocol for dread that shows up early",
    category: "Anxiety & Overwhelm",
    excerpt: "That low hum of dread by 4pm on Sunday has a shape. Here is a kind, four-step way to walk it back into the weekend you still have.",
    read_time: "5 min",
    author: "The Back of the Mind team",
    published_at: "2025-10-19",
    content: [
      "The Sunday scaries are not a character flaw. They are a rehearsal — your mind running Monday on a loop before it exists. Rehearsal without a script just repeats the fear.",
      "Step one: give the week a shape. Fifteen minutes on Sunday evening, write the three things that actually matter for Monday. Not twenty. Three.",
      "Step two: close the open loops. One message you've been avoiding, one dish, one stretched breath outside. Loops, not loads, are what keep the hum alive.",
      "Step three: keep one hour of Sunday aggressively for yourself. Step four: if Monday keeps breaking in, write down the exact thought and bring it to session. Dread with an audience shrinks."
    ]
  },
  {
    slug: "burnout-is-not-a-badge",
    title: "Burnout is not a badge: rebuilding after the long-overdue crash",
    category: "Workplace Burnout",
    excerpt: "Burnout doesn't announce itself — it invoices you later. On the quiet maths of depletion, and how recovery is scheduled, not wished for.",
    read_time: "7 min",
    author: "The Back of the Mind team",
    published_at: "2025-11-18",
    content: [
      "Somewhere along the way, exhaustion became a status symbol. The 11pm email, the skipped lunch, the 'crazy week!' said with a smile. Burnout rarely announces itself — it invoices you later, with interest.",
      "The tell is not tiredness; it's detachment. When you stop caring about work you used to find meaningful, that is not laziness. That is a system protecting itself by going offline.",
      "Rebuild in this order: body first (sleep, food, movement), then boundaries (one honest 'no' per week), then meaning (one task a day you'd choose even if nobody watched).",
      "And drop the myth that rest must be earned. Rest is not the reward for finishing the work. Rest is the thing that lets the work be finished at all."
    ]
  },
  {
    slug: "saying-no-at-work",
    title: "Saying no at work without writing a resignation letter",
    category: "Workplace Burnout",
    excerpt: "Boundaries at work sound scary until they have sentences. Four scripts that protect your calendar and your relationships at the same time.",
    read_time: "5 min",
    author: "The Back of the Mind team",
    published_at: "2025-09-30",
    content: [
      "Most overload is not caused by villains. It's caused by a missing sentence. The sentence is: 'I can't take this on right now — I can do it Thursday / next sprint / after the launch.'",
      "Script one, the swap: 'Not this week, but I can on Tuesday.' You are saying no to the timing, not the person.",
      "Script two, the trade: 'If this becomes the priority, what should move?' Suddenly the decision is shared, and sharable is survivable.",
      "Script three, the honest ceiling: 'I'm at capacity — anything new means something slips.' Said warmly, once, it resets expectations for months. Boundaries are not walls. They are doors with handles on your side."
    ]
  },
  {
    slug: "on-your-own-side",
    title: "The quiet skill of being on your own side",
    category: "Self-Compassion",
    excerpt: "You'd never talk to a friend the way you talk to yourself at 1am. On the odd, trainable art of self-compassion — minus the incense and clichés.",
    read_time: "6 min",
    author: "The Back of the Mind team",
    published_at: "2025-10-07",
    content: [
      "There is a voice in your head that narrates your mistakes in exquisite detail. Here's the experiment: would you say that sentence, out loud, to a friend who just failed the same way? No? Then it isn't feedback. It's a habit.",
      "Self-compassion is not lowering the bar. It's changing the tone of the coach. Research keeps finding the same thing: people who treat themselves kindly after failure try again sooner, not less.",
      "Start small: replace 'what is wrong with me' with 'what would help right now'. One question attacks. The other one plans.",
      "And when the inner critic gets loud, put a hand somewhere on your chest and breathe once, slowly. It sounds silly until you notice the critic needs a body to calm down, too."
    ]
  },
  {
    slug: "need-a-break-not-a-fix",
    title: "You sound like someone who needs a break, not a fix",
    category: "Self-Compassion",
    excerpt: "Some seasons don't need a new system, a new app, or a new you. They need an afternoon with the phone face-down and the guilt evicted.",
    read_time: "4 min",
    author: "The Back of the Mind team",
    published_at: "2025-08-22",
    content: [
      "We live in a culture that answers tiredness with optimisation. You say you're drained; the world sells you a planner, a habit tracker, a new morning routine.",
      "But some states are not problems to solve. They are signals to obey. A plant with drooping leaves doesn't need a better strategy. It needs water, and a bit of sun.",
      "Try the radical version: one afternoon, deliberately average. No wins, no output, nothing to show. Then notice how the interesting thoughts come back on their own, like cats.",
      "If the guilt shows up — and it will — that's not proof the break was wrong. It's proof you needed one more than you admitted."
    ]
  },
  {
    slug: "ten-minutes-of-noticing",
    title: "Ten minutes of noticing: mindfulness for people sure it isn't for them",
    category: "Mindfulness Practices",
    excerpt: "You don't have to empty your mind. You just have to notice it. A beginner's practice with zero incense and one rule: expect nothing, count anyway.",
    read_time: "6 min",
    author: "The Back of the Mind team",
    published_at: "2025-11-25",
    content: [
      "The most common reason people quit meditation is the belief that they're bad at it. But a busy mind during practice is not failure — it is the practice. Noticing the wandering is the rep.",
      "Start with ten minutes and one anchor: the feeling of breath at the tip of the nose. Mind wanders? Fine. Say 'thinking', softly, and come back. That's one. Count ten of those and you're done, whether the mind was busy or not.",
      "No cushion required. Eyes open is fine. The metro is fine. Mindfulness is not a room; it's a relationship with your own attention.",
      "Do it daily for two weeks before judging. The effects arrive sideways: a slightly longer pause before reacting, a slightly softer Monday."
    ]
  },
  {
    slug: "walking-meditations",
    title: "Walking meditations for overactive minds",
    category: "Mindfulness Practices",
    excerpt: "For minds that won't sit still: take the practice outside. Steps as breath, curbs as metronomes, and the street as a moving temple.",
    read_time: "5 min",
    author: "The Back of the Mind team",
    published_at: "2025-07-15",
    content: [
      "Some nervous systems hear 'sit still and breathe' and respond with a brainstorm. If that's you, walk. Walking meditation is fully legitimate practice — the ground simply replaces the breath as the anchor.",
      "Match attention to steps: for thirty paces, notice the contact of each foot. For the next thirty, the sounds arriving. Then thirty for what the eyes drink in, without naming it.",
      "The mind will interrupt with solutions to problems you forgot you had. Let them queue. The walk will still be there after they've been considered.",
      "Ten minutes counts. The point was never the posture. The point is to come back to the body, on purpose, kindly, again and again."
    ]
  }
];
